import { ChangeDetectionStrategy, Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef, NgZone, Output, EventEmitter } from '@angular/core';
import { BaseInputComponent } from '../base-input/base-input.component';
import { filter, Subject, takeUntil } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CreativeUnitPackageService } from "../../../../_core/state/ad/ad-unit-package.service";
import { MergeTags } from "../../../../../../../api/src/_core/utils/utils.merge-tags";
import { MatDialog } from '@angular/material/dialog';
import { CodeEditorDialogComponent, CodeEditorDialogData } from '../code-editor-dialog/code-editor-dialog.component';
import { FreshWorksWidgetsService } from '../../../../_core/services/freshworks-widgets.service';

@Component({
	selector: 'app-monaco-input',
	templateUrl: './monaco-input.component.html',
	styleUrls: ['./monaco-input.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MonacoInputComponent extends BaseInputComponent implements OnInit, OnChanges, OnDestroy {
	@Input() editorOptions = {
		theme: 'vs-dark',
		language: 'scss',
		automaticLayout: true,
		fixedOverflowWidgets: true
	};
	@Input() hideLabel: boolean = false;
	@Input() language: string = 'json';
	@Input() code: string = '';
	@Input() debounceTime: number = 25; // For non-JSON formats it's recommended set it around 500ms

	@Input() showFormatButton: boolean = false;
	@Input() showValidationError: boolean = false;
	@Input() mergeTagState: { [key: string]: any };
	@Input() showAiSuggestions: boolean = false;
	/** Custom prompt rules to pass to the AI suggestions component */
	@Input() aiCustomPrompt: string = '';

	@Output() validationError: EventEmitter<string> = new EventEmitter<string>();

	public isCurrentValueObject = false; // Save the type of the current value to know if it is an object or not.
	public editor: any;
	public isLoading = true; // Track loading state for preloader

	private preventNextUpdate = false;
	private preventNextEmit = false;
	private valueChangeSubject = new Subject<any>();
	private themeObserver: MutationObserver;

	public incorrectInput = false;

	constructor(
		private cdr: ChangeDetectorRef,
		private ngZone: NgZone,
		private creativeUnitPackageService: CreativeUnitPackageService,
		private dialog: MatDialog,
		private freshWorksService: FreshWorksWidgetsService
	) {
		super();
	}

	ngOnInit(): void {
		this.valueChangeSubject
			.pipe(
				debounceTime(this.debounceTime),
				filter(_ => !this.preventNextEmit),
				takeUntil(this._unsubscribe)
			)
			.subscribe(value => {
				this.emitValueChangeDebounced(value);
			});

		if (this.language) {
			this.editorOptions = {
				...this.editorOptions,
				language: this.language
			};
		}

		// Set initial theme based on body class
		this.updateTheme();

		// Watch for theme changes on body element
		this.setupThemeObserver();

		this.initializeCurrentValue();
		this.initializeForm();
	}

	ngOnDestroy(): void {
		// Clean up theme observer
		if (this.themeObserver) {
			this.themeObserver.disconnect();
		}
		super.ngOnDestroy();
	}

	/**
	 * Detects if the body element has a light theme class
	 */
	private isLightTheme(): boolean {
		const body = document.body;
		return body.classList.contains('light-theme') || body.classList.contains('theme-light');
	}

	/**
	 * Updates the Monaco editor theme based on the current body theme
	 */
	private updateTheme(): void {
		const theme = this.isLightTheme() ? 'vs' : 'vs-dark';
		this.editorOptions = {
			...this.editorOptions,
			theme
		};

		// If editor is already initialized, update its theme
		if (this.editor) {
			this.ngZone.run(() => {
				(window as any).monaco?.editor.setTheme(theme);
				this.cdr.detectChanges();
			});
		}
	}

	/**
	 * Sets up a MutationObserver to watch for theme changes on the body element
	 */
	private setupThemeObserver(): void {
		this.themeObserver = new MutationObserver(() => {
			this.updateTheme();
		});

		this.themeObserver.observe(document.body, {
			attributes: true,
			attributeFilter: ['class']
		});
	}

	private initializeCurrentValue(): void {
		if (!this.currentValue && this.parentForm?.get(this.controlName).value) {
			this.currentValue = this.parentForm?.get(this.controlName).value;
		}

		if (this.language === 'json') {
			this.isCurrentValueObject = true;

			// console.log('initializeCurrentValue json', this.currentValue);
			if (this.currentValue) {
				try {
					this.currentValue = JSON.stringify(this.currentValue, null, 2);
				} catch (e) {
					console.log('error parsing');
					this.currentValue = null;
				}
			} else {
				this.currentValue = null;
			}
		}

		this.validate(this.currentValue);
		console.log('initializeCurrentValue', this.currentValue);
	}

	ngOnChanges(changes: SimpleChanges): void {
		// console.log('onChanges', changes, this.currentValue, this.form?.get(this.controlName).value);
		if (changes.currentValue) {
			// if a value is in hold to be emitted, keep it on pause as we could wrongly overwrite data
			this.preventNextEmit = true;

			if (changes.currentValue?.firstChange) {
				this.preventNextUpdate = false;
			}

			// -- object --
			if (typeof this.currentValue === 'object') {
				// console.log('onChanges currentValue is OBJECT', this.currentValue);
				this.isCurrentValueObject = true;

				if (!changes.currentValue.firstChange) {
					// if not, we need to compare the JSON stringified values to see if they are different
					let previousValue = changes.currentValue.previousValue;
					let comparisonPreviousValue: string;
					let comparisonCurrentValue: string;

					try {
						if (typeof previousValue === 'object') {
							comparisonPreviousValue = JSON.stringify(previousValue, null, 2);
						} else comparisonPreviousValue = previousValue;

						comparisonCurrentValue = JSON.stringify(this.currentValue, null, 2);
					} catch (e) {
						console.log('onChanges error while parsing', e);
						return;
					}

					if (comparisonPreviousValue !== comparisonCurrentValue) {
						this.currentValue = JSON.stringify(this.currentValue, null, 2);
						changes.currentValue.currentValue = this.currentValue;
					} else this.preventNextUpdate = true;
				}
			} else {
				this.isCurrentValueObject = false;

				if (!changes.currentValue.firstChange) {
					if (this.currentValue.trim() !== changes.currentValue?.previousValue?.trim()) {
						this.preventNextUpdate = false;
					}
				}
			}
		}

		if (changes.currentValue && !changes.currentValue.firstChange && !this.preventNextUpdate) {
			// Test if changes are the same as the current editor value.
			if (this.editor && this.editor.getValue() !== changes.currentValue.currentValue) {
				this.patchParentForm(changes.currentValue.currentValue);
				this.validate(changes.currentValue.currentValue);
				super.ngOnChanges(changes);
			}
		}

		this.preventNextUpdate = false;
	}

	onEditorInit(editor: any) {
		this.editor = editor;
		this.isLoading = false; // Hide preloader once editor is initialized
		this.cdr.detectChanges(); // Trigger change detection to update the view

		editor.onDidChangeModelContent(() => {
			this.ngZone.run(() => {
				// console.log('onDidChangeModelContent', editor.getValue(), this.currentValue);

				// if we are expecting an object, we need to compare the JSON stringified values to see if they are different
				if (this.isCurrentValueObject && this.currentValue) {
					let comparisonEditorValue: string;
					let comparisonCurrentValue: string;
					let parsedEditorValue: object;

					try {
						if (typeof editor.getValue() === 'object') {
							parsedEditorValue = editor.getValue();
						} else {
							parsedEditorValue = JSON.parse(editor.getValue());
						}

						comparisonEditorValue = JSON.stringify(parsedEditorValue, null, 2);
					} catch (e) {
						console.log('error parsing monaco editor value', e);
						this.incorrectInput = true;
						this.cdr.detectChanges();
						return;
					}

					try {
						if (typeof this.currentValue === 'object') {
							comparisonCurrentValue = JSON.stringify(this.currentValue, null, 2);
						} else {
							comparisonCurrentValue = JSON.stringify(JSON.parse(this.currentValue), null, 2);
						}
					} catch (e) {
						comparisonCurrentValue = '';
						console.log('error parsing currentValue', e);
					}

					this.incorrectInput = false; // if we get this far everything parsed correctly

					// if they are different, we can safely emit the value
					if (comparisonEditorValue !== comparisonCurrentValue) {
						this.preventNextEmit = false;
						this.valueChangeSubject.next(parsedEditorValue);
					}
				} else {
					// if they are strings a regular comparison will suffice
					if (editor.getValue()?.trim() !== this.currentValue?.trim()) {
						this.preventNextEmit = false;
						this.valueChangeSubject.next(editor.getValue());
					}
				}

				this.cdr.detectChanges();
			});
		});
	}

	private emitValueChangeDebounced(value: any): void {
		this.patchParentForm(value);

		if (!this.preventNextUpdate) {
			this.preventNextUpdate = true;

			this.validate(value);
			super.emitValueChange(value);
		} else {
			this.preventNextUpdate = false;
		}
	}

	public formatAndValidate() {
		if (!this.editor) return;

		try {
			if (this.language === 'json') {
				try {
					this.editor.setValue(JSON.stringify(JSON.parse(this.editor.getValue()), null, 2));
				} catch (e) {
					console.log('Invalid JSON input: ', this.editor.getValue());
				}
			}

			// editor format action
			this.editor.getAction('editor.action.formatDocument').run();
		} catch (error) {
			console.log(error);
			return;
		}
	}

	private patchParentForm(value: any): void {
		if (this.patchChanges && this.parentForm) {
			console.log('patching parent form', this.controlName, value);
			this.parentForm.patchValue({ [this.controlName]: value });
		}
	}

	private validate(value: any) {
		if (this.language === 'scss') {
			let parsed = this.mergeTagState ? MergeTags.applyMergeTagsToString(value, this.mergeTagState) : value;
			this.creativeUnitPackageService.validateSass(parsed).then(_ => {
				this.validationError.emit(null);
			}).catch(e => {
				this.validationError.emit(e.message || e);
				console.warn('Invalid SCSS input: ', e.message);
			});
		}
	}

	/**
	 * Handle AI code suggestion being applied
	 */
	onAiCodeApplied(event: { suggestedCode: string; changes: string[] }): void {
		if (!this.editor) return;

		// Update the editor with the suggested code
		this.editor.setValue(event.suggestedCode);
		this.preventNextEmit = false;

		// Parse if JSON
		let valueToEmit = event.suggestedCode;
		if (this.language === 'json') {
			try {
				valueToEmit = JSON.parse(event.suggestedCode);
			} catch (e) {
				console.log('AI suggestion produced invalid JSON:', e);
			}
		}

		this.valueChangeSubject.next(valueToEmit);
	}

	/**
	 * Handle AI code suggestion being reverted
	 */
	onAiCodeReverted(originalCode: string): void {
		if (!this.editor) return;

		// Restore the original code
		this.editor.setValue(originalCode);
		this.preventNextEmit = false;

		// Parse if JSON
		let valueToEmit = originalCode;
		if (this.language === 'json') {
			try {
				valueToEmit = JSON.parse(originalCode);
			} catch (e) {
				console.log('Reverted code is invalid JSON:', e);
			}
		}

		this.valueChangeSubject.next(valueToEmit);
	}

	public openFullscreenEditor(): void {
		this.freshWorksService.hideFreshWorksWidgets();

		const dialogData: CodeEditorDialogData = {
			code: this.editor ? this.editor.getValue() : this.currentValue,
			language: this.language,
			editorOptions: this.editorOptions,
			title: this.label || 'Code Editor'
		};

		const dialogRef = this.dialog.open(CodeEditorDialogComponent, {
			// if changing classes, make sure to update the css as well
			panelClass: ['fullscreen', 'background-color', 'max-width-lg', 'fullscreen-code-editor'],
			data: dialogData,
			disableClose: false
		});

		dialogRef.afterClosed().subscribe(result => {
			this.freshWorksService.showFreshWorksWidgets();

			if (result !== undefined && result !== null) {
				// Update the editor value with the result from the dialog
				if (this.editor) {
					this.editor.setValue(result);
				} else {
					this.currentValue = result;
				}

				// Emit the value change
				this.preventNextEmit = false;

				// parse the value before emitting if JSON editor
				if (this.language === 'json') {
					try {
						result = JSON.parse(result);
					} catch (e) {
						console.log('Invalid JSON input from dialog: ', result);
					}
				}

				this.valueChangeSubject.next(result);
			}
		});
	}
}
